import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle, Loader2, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import { logActivity } from "@/lib/activityLogger";
import type { MusicianCheckIn, EventCheckIn } from "./types";

interface Props {
  checkIn: EventCheckIn;
  musicians: MusicianCheckIn[];
  inquiryId: string;
  onRefresh: () => void;
}

export function DebriefCompleteCard({ checkIn, musicians, inquiryId, onRefresh }: Props) {
  const storageKey = `ops_debrief_${checkIn.id}`;
  const loadDraft = useCallback(() => {
    try { return JSON.parse(sessionStorage.getItem(storageKey) || "{}"); } catch { return {}; }
  }, [storageKey]);
  const persistDraft = useCallback((field: string, value: string | boolean) => {
    const saved = loadDraft();
    sessionStorage.setItem(storageKey, JSON.stringify({ ...saved, [field]: value }));
  }, [storageKey, loadDraft]);

  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(() => loadDraft().showForm || false);
  const [eventNotes, setEventNotes] = useState(() => loadDraft().eventNotes || "");
  const [incidentReport, setIncidentReport] = useState(() => loadDraft().incidentReport || "");

  const allWrapped = musicians.length > 0 && musicians.every(m => m.wrapped_up_at || m.arrival_status === "no_show");

  const handleComplete = async () => {
    if (eventNotes.length < 20) {
      toast.error(`Event notes must be at least 20 characters (${eventNotes.length}/20)`);
      return;
    }
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const hasIncident = incidentReport.trim().length > 0;

      // Save event completion
      const { error } = await supabase
        .from("event_check_ins")
        .update({
          completed_at: now,
          status: "completed",
          event_notes: eventNotes,
          incident_report: hasIncident ? incidentReport : null,
          has_incident: hasIncident,
        })
        .eq("id", checkIn.id);
      if (error) throw error;

      // If incident, flag inquiry + log activity + insert CRM email
      if (hasIncident) {
        // Flag inquiry
        await supabase
          .from("contact_submissions")
          .update({ has_incident: true } as any)
          .eq("id", inquiryId);

        // Log activity
        await logActivity({
          inquiryId,
          action: "incident_reported",
          description: `Incident reported: ${incidentReport.slice(0, 80)}...`,
          metadata: { event_check_in_id: checkIn.id, event_date: checkIn.event_date },
        });

        // Auto-insert incident email into CRM thread
        const noShowMusicians = musicians.filter(m => m.arrival_status === "no_show");
        const noShowSection = noShowMusicians.length > 0
          ? `<h3 style="color:#dc2626">No-Show Musicians</h3><ul>${noShowMusicians.map(m => `<li>${m.musician_name} (${m.instrument})</li>`).join("")}</ul>`
          : "";

        const bodyHtml = `
          <div style="font-family:sans-serif;max-width:600px">
            <h2 style="color:#dc2626">⚠️ Incident Report — ${format(new Date(checkIn.event_date + "T00:00:00"), "MMM d, yyyy")}</h2>
            ${noShowSection}
            <h3>Incident Details</h3>
            <p>${incidentReport.replace(/\n/g, "<br/>")}</p>
            <h3>Event Notes</h3>
            <p>${eventNotes.replace(/\n/g, "<br/>")}</p>
            <hr/>
            <p style="font-size:11px;color:#666">Auto-generated by On-Site Ops at ${format(new Date(), "PPpp")}</p>
          </div>
        `;

        await supabase.from("inquiry_emails").insert({
          inquiry_id: inquiryId,
          recipient_email: "internal@theloststrings.com",
          subject: `⚠️ Incident Report — Event ${format(new Date(checkIn.event_date + "T00:00:00"), "MMM d")}`,
          body_html: bodyHtml,
          status: "sent",
          template_used: "incident_report",
        });
      }

      sessionStorage.removeItem(storageKey);
      toast.success("Event completed!");
      onRefresh();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (checkIn.completed_at) {
    return (
      <div className="space-y-1">
        <Badge className="bg-green-500/15 text-green-700 border-green-500/30">
          <CheckCircle className="h-3 w-3 mr-1" /> Completed at {format(new Date(checkIn.completed_at), "h:mm a")}
        </Badge>
        {checkIn.has_incident && (
          <Badge className="bg-destructive/15 text-destructive border-destructive/30 ml-2">
            <AlertTriangle className="h-3 w-3 mr-1" /> Incident Reported
          </Badge>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {!allWrapped && (
        <p className="text-[10px] text-amber-600">All musicians must be wrapped up before completing.</p>
      )}

      {!showForm ? (
        <Button className="w-full" disabled={!allWrapped} onClick={() => { setShowForm(true); persistDraft("showForm", true); }}>
          <CheckCircle className="h-4 w-4 mr-2" /> Complete Event
        </Button>
      ) : (
        <div className="space-y-3 p-3 border rounded-md">
          <div>
            <label className="text-xs font-medium">Overall Event Notes *</label>
            <Textarea
              placeholder="Summary of the event (min 20 characters)"
              rows={3}
              value={eventNotes}
              onChange={e => { setEventNotes(e.target.value); persistDraft("eventNotes", e.target.value); }}
              className="text-xs mt-1"
            />
            <p className={`text-[9px] mt-0.5 ${eventNotes.length >= 20 ? "text-green-600" : "text-amber-600"}`}>
              {eventNotes.length}/20 characters
            </p>
          </div>
          <div>
            <label className="text-xs font-medium flex items-center gap-1">
              <AlertTriangle className="h-3 w-3 text-destructive" /> Issues / Incidents (optional)
            </label>
            <Textarea
              placeholder="Equipment failure, no-shows, venue issues..."
              rows={2}
              value={incidentReport}
              onChange={e => { setIncidentReport(e.target.value); persistDraft("incidentReport", e.target.value); }}
              className="text-xs mt-1"
            />
          </div>
          <Button
            className="w-full"
            disabled={saving || eventNotes.length < 20}
            onClick={handleComplete}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
            Finalize & Complete
          </Button>
        </div>
      )}
    </div>
  );
}
